import {
  ARTIFACT_TRANSPORT_MAX_BYTES,
  type ArtifactContext,
  type ArtifactRecordTransport,
  artifactRecords,
  canonicalize,
  readArtifact,
  sealArtifact,
} from '@viko/crypto'
import {
  type ArtifactIndexEntry,
  ContentPutResult,
  EncryptedContent,
  EncryptionContext,
} from '@viko/protocol'
import type { ContentVault } from './content-vault.js'

// ---- j-privacy: S05 malicious artifacts ----
/**
 * Passive media a blob URL may be typed as. Anything else (HTML, SVG, XML, PDF, scripts, unknown)
 * gets `application/octet-stream` and is download-only: an artifact written by a (possibly
 * prompt-injected) agent never becomes an app-origin document, whatever its sealed MIME says.
 */
const PREVIEW = /^(image\/(png|jpeg|gif|webp|avif)|video\/(mp4|webm))$/
export function previewType(mime: string): string | undefined {
  const m = mime.split(';')[0]?.trim().toLowerCase() ?? ''
  return PREVIEW.test(m) ? m : undefined
}
// ---- end j-privacy ----

/**
 * Where a New session's attachments are addressed. The run is unknown to the hub until vikod
 * reports it, and the hub admits a machine to a run's records only once that run is its own, so
 * `run.start` attachments go to the target machine's namespace instead (found by M13 reliability:
 * with the run id they failed `run_not_on_machine`).
 */
export const newSessionAttachmentScope = (machineId: string) => `machine:${machineId}`

/** Only trusted decrypted projections/commands may supply these coordinates. */
export type EncryptedArtifactRef = ArtifactContext
export class BrowserArtifacts {
  private closed = false
  private readonly handles = new Set<() => void>()
  /** Call on logout/account switch/forget-device, and construct a new reader after unlock. */
  lock() {
    this.closed = true
    for (const dispose of this.handles) dispose()
  }
  private requireOpen() {
    if (this.closed) throw new Error('content locked')
  }
  constructor(
    private readonly o: {
      accountId: string
      vault: Pick<ContentVault, 'epochKey'>
      currentKey(): Promise<{ epoch: number; key: CryptoKey }>
      transport: ArtifactRecordTransport
      /** Sealed index entry for the Artifacts tab, written once the ciphertext is stored. */
      index?: (entry: ArtifactIndexEntry) => Promise<unknown>
    },
  ) {}
  /** Prepare once; the caller retains this operation across uncertain transport outcomes. */
  async prepare(file: File, runId: string) {
    this.requireOpen()
    if (file.size > ARTIFACT_TRANSPORT_MAX_BYTES)
      throw new Error('Encrypted attachments are limited to 32 MiB.')
    const { key, epoch } = await this.o.currentKey()
    const ref: EncryptedArtifactRef = {
      version: 1,
      accountId: this.o.accountId,
      epoch,
      runId,
      objectId: `art_${crypto.randomUUID()}`,
    }
    const bytes = new Uint8Array(await file.arrayBuffer())
    if (bytes.length > ARTIFACT_TRANSPORT_MAX_BYTES)
      throw new Error('Encrypted attachments are limited to 32 MiB.')
    this.requireOpen()
    const sealed = await sealArtifact(key, ref, bytes, {
      name: file.name,
      mime: file.type || 'application/octet-stream',
    })
    const records = artifactRecords(ref, sealed)
    const entry: ArtifactIndexEntry = {
      ref,
      name: file.name || 'attachment',
      mime: file.type || 'application/octet-stream',
      size: bytes.length,
      runId,
      createdAt: Date.now(),
    }
    let indexed = false
    return {
      ref,
      upload: async () => {
        this.requireOpen()
        if ((await this.o.currentKey()).epoch !== epoch)
          throw new Error('artifact key synchronization required')
        for (const record of records) {
          this.requireOpen()
          await this.o.transport.put(record)
        }
        // Best effort: the attachment itself is delivered either way; a retry re-indexes.
        if (!indexed && this.o.index)
          indexed = await this.o.index(entry).then(
            () => true,
            () => false,
          )
        return ref
      },
    }
  }
  async read(ref: EncryptedArtifactRef) {
    this.requireOpen()
    if (ref.accountId !== this.o.accountId) throw new Error('wrong artifact account')
    const data = await readArtifact(
      await this.o.vault.epochKey(ref.accountId, ref.epoch),
      ref,
      this.o.transport,
    )
    this.requireOpen()
    return data
  }
  /** Full authentication precedes URL creation. Consumers must dispose on replacement/unmount/logout. */
  async open(ref: EncryptedArtifactRef) {
    const data = await this.read(ref)
    this.requireOpen()
    const preview = previewType(data.mime)
    // `blob` is read in place (blob.text()): fetching a blob: URL breaks connect-src.
    const blob = new Blob([data.bytes], { type: preview ?? 'application/octet-stream' })
    const url = URL.createObjectURL(blob)
    let disposed = false
    const dispose = () => {
      if (!disposed) {
        disposed = true
        URL.revokeObjectURL(url)
        this.handles.delete(dispose)
      }
    }
    this.handles.add(dispose)
    const opened: {
      url: string
      blob?: Blob
      name: string
      mime: string
      preview: string | undefined
      dispose: () => void
    } = { url, blob, name: data.name, mime: data.mime, preview, dispose }
    return opened
  }
}

/** Same authenticated content endpoints, with an actual streamed response cap (not Content-Length). */
export function browserArtifactTransport(
  fetcher: typeof fetch,
  origin = '',
): ArtifactRecordTransport {
  async function request(method: 'put' | 'get', body: unknown) {
    const serialized = JSON.stringify(body)
    if (new TextEncoder().encode(serialized).byteLength > 1_410_000)
      throw new Error('artifact record too large')
    const response = await fetcher(`${origin}/api/content/${method}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: serialized,
    })
    if (!response.ok || !response.body) {
      await response.body?.cancel()
      throw new Error('artifact transport unavailable')
    }
    const reader = response.body.getReader()
    const parts: Uint8Array[] = []
    let size = 0
    try {
      for (;;) {
        const part = await reader.read()
        if (part.done) break
        size += part.value.byteLength
        if (size > (method === 'put' ? 4096 : 1_410_000)) {
          await reader.cancel()
          throw new Error('artifact response too large')
        }
        parts.push(part.value)
      }
    } finally {
      reader.releaseLock()
    }
    const bytes = new Uint8Array(size)
    let offset = 0
    for (const part of parts) {
      bytes.set(part, offset)
      offset += part.length
    }
    return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes)) as unknown
  }
  return {
    put: async (raw) => ContentPutResult.parse(await request('put', EncryptedContent.parse(raw))),
    get: async (raw) => {
      const expected = EncryptionContext.parse(raw)
      const record = EncryptedContent.parse(await request('get', expected))
      if (canonicalize(record.context) !== canonicalize(expected))
        throw new Error('wrong artifact context')
      return record
    },
  }
}
